<template>
  <div class="ling-chart-container">
    <svg version="1.1" xmlns="http://www.w3.org/2000/svg" :width="computedStyle.width" :height="computedStyle.height" :viewBox="`0 0 ${computedStyle.width} ${computedStyle.height}`">
      <g class="ling-chart-background">
        <path
          v-for="(point, index) in circlePointsAt(circleRadius)"
          :key="`r${index}`"
          :stroke-width="computedStyle.radialLineStrokeWidth"
          class="ling-grid-line-radial"
          :d="lineBetween(circleCenter.x, circleCenter.y, point.x, point.y)"
        />
        <path
          v-for="(line, index) in circleChordLines"
          :key="`f${index}`"
          :stroke-width="computedStyle.chordLineStrokeWidth"
          class="ling-grid-line-chord"
          :d="lineBetween(line.from.x, line.from.y, line.to.x, line.to.y)"
        />
      </g>
      <g class="ling-chart-area">
        <template
          v-for="(points, index) in areas"
        >
          <polyline
            :key="`a${index}`"
            :points="points"
            :stroke-width="computedStyle.areaStrokeWidth"
            :id="`${uid}-ling-chart-area-${index}`"
            :style="areaStyle(index)"
          />
        </template>
      </g>
    </svg>
    <div class="ling-chart-area-hover">
      <b-tooltip
        v-for="(points, aIndex) in areas"
        :key="`at${aIndex}`"
        :target="`${uid}-ling-chart-area-${aIndex}`"
        placement="auto"
      >
        <slot name="area" v-bind:index="aIndex"/>
      </b-tooltip>
    </div>
    <div class="ling-chart-legend">
      <template
        v-for="(point, index) in circlePointsAt(circleRadius)"
      >
        <div
          class="ling-chart-legend-point"
          :key="`l${index}`"
          :style="`left:${point.x}px;top:${point.y}px`"
          v-if="typeof categories[index] !== 'undefined'"
        >
          <span class="ling-chart-legend-label-container">
            <span
              class="ling-chart-legend-label"
              :class="{'label-left': point.x < circleCenter.x, 'label-top': point.y < circleCenter.y}"
            >
              <slot name="label" v-bind:index="index">
                <span
                  v-for="(current, sIndex) in series"
                  :key="`ll${sIndex}`"
                >
                  {{ typeof current[index] !== 'undefined' ? current[index].value : '' }}
                </span>
              </slot>
            </span>
          </span>
        </div>
      </template>
    </div>
  </div>
</template>
<style lang="scss">
  .ling-chart-container {
    position: relative;

    .ling-chart-legend-point {
      position: absolute;

      .ling-chart-legend-label-container {
        position: relative;

        .ling-chart-legend-label {
          position: absolute;

          &.label-top {
            bottom: 0px;
          }

          &.label-left {
            right: 0px;
          }
        }
      }
    }
  }

  .ling-chart-area polyline {
    fill-opacity: 0.6;
    transition: fill-opacity .3s ease-in-out;

    &:hover {
      fill-opacity: 0.8;
    }
  }
  .ling-chart-background path {
    stroke: var(--gray);
    opacity: 0.3;
  }
</style>
<script>
  const DEFAULT_CHART_STYLE = {
    chordStep: 5,
    height: 400,
    width: 439,
    scale: 0.8,
    areaStrokeWidth: 3,
    areaStrokeColor: 'var(--lingusta-green)',
    areaFillColor: 'var(--lingusta-green)',
    chordLineStrokeWidth: 1,
    radialLineStrokeWidth: 2,
    areas: []
  }

  export default {
    components: {
    },
    props: {
      categories: {
        type: Array,
        required: true,
        default: () => ([])
      },
      series: {
        type: Array,
        required: true,
        default: () => ([[]])
      },
      chartStyles: {
        type: Object,
        default: () => (DEFAULT_CHART_STYLE)
      }
    },
    computed: {
      computedStyle() {
        return _.assign({}, DEFAULT_CHART_STYLE, this.chartStyles)
      },
      areas() {
        var areas = []

        for(var j = 0; j < this.series.length; j++) {
          var actual = this.series[j]
          var points = []

          for(var i = 0; i < actual.length; i++) {
            var pointsForSeries = this.circlePointsAt(this.circleRadius * actual[i].value);
            points.push(pointsForSeries[i])
          }

          if(actual.length > 0) {
            var pointsForSeries = this.circlePointsAt(this.circleRadius * actual[0].value);
            points.push(pointsForSeries[0])
          }

          areas.push(_.reduce(points, (result, point) => `${result} ${point.x} ${point.y}`, ''))
        }

        return areas
      },
      circleChordLines() {
        var lines = [];
        var step = this.circleRadius / this.computedStyle.chordStep

        for(var j = 0; j < this.computedStyle.chordStep + 1; j++) {
          var bounds = this.circlePointsAt(step * j)
          var prev = bounds[0]

          for(var i = 1; i < bounds.length; i++) {
            lines.push({
              from: prev,
              to: bounds[i]
            })
            prev = bounds[i]
          }

          lines.push({
            from: prev,
            to: bounds[0]
          })
        }

        return lines
      },
      circleRadius() {
        return Math.min(this.computedStyle.width / 2, this.computedStyle.height / 2) * this.computedStyle.scale
      },
      circleCenter() {
        return {
          x: this.computedStyle.width / 2,
          y: this.computedStyle.height / 2
        }
      }
    },
    methods: {
      areaStyle(index) {
        var defaultStyle = {
          fill: this.computedStyle.areaFillColor,
          stroke: this.computedStyle.areaStrokeColor
        }

        if(typeof this.computedStyle.areas[index] !== 'undefined') {
          return _.defaults(this.computedStyle.areas[index], defaultStyle)
        } else {
          return defaultStyle
        }
      },
      lineBetween(fromX, fromY, toX, toY) {
        return `M ${fromX} ${fromY} L ${toX} ${toY}`
      },
      circlePointsAt(r) {
        var step = Math.PI * 2 / this.categories.length
        var points = []

        var {x, y} = this.circleCenter

        for(var i = Math.PI * 3; i >= Math.PI; i-=step) {
          points.push({
            x: x + (Math.sin(i) * r),
            y: y + (Math.cos(i) * r)
          })
        }

        return points
      }
    }
  }
</script>